import { Api } from '@/api/api';
import { HttpClient } from '@/api/generatedApi';
import { getApiBasePath, getUiBasePathPrefix } from '@/utils/basePath';
import { PATHS } from '@/utils/paths';
import { getAuthToken } from './auth/getAuthToken';

/**
 * Full API base URL for all API calls (XHR, fetch, iframe src).
 * - ECS: VITE_API_BASE_URL is the API domain (e.g., https://dev3-api.physna.com) — required
 *   because UI and API are separate origins.
 * - EKS: VITE_API_BASE_URL is empty — nginx proxies API requests, so relative path works.
 * - Local dev: VITE_API_BASE_URL is http://localhost:3000 — Vite proxy forwards to API server.
 */
export const getBaseUrl = () => {
  const basePath = getApiBasePath() || '/v3';
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  const isResolved = apiBaseUrl && !apiBaseUrl.startsWith('VITE_RUNTIME_');
  return isResolved ? `${apiBaseUrl}${basePath}` : basePath;
};

export const getApiDocumentationUrl = () => `${getBaseUrl()}/docs`;

let isRedirectInProgress = false;

const createHttpClient = (): HttpClient => {
  const client = new HttpClient({
    baseURL: getBaseUrl(),
    secure: true,
    withCredentials: true,
    securityWorker: async () => {
      const authToken = await getAuthToken();
      return {
        headers: {
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      };
    },
  });

  if (client.instance.interceptors?.response) {
    client.instance.interceptors.response.use(
      response => response,
      async error => {
        const skipRedirect = error.config?.meta?.skipRedirect;
        if (error.response && !isRedirectInProgress && !skipRedirect) {
          if (error.response.status === 401) {
            isRedirectInProgress = true;
            localStorage.removeItem('authToken');
            const prefix = getUiBasePathPrefix();
            const isOnExceptionPage = redirectExceptions.some(path =>
              globalThis.location.pathname.startsWith(`${prefix}${path}`)
            );
            if (isOnExceptionPage) {
              isRedirectInProgress = false;
            } else if (!globalThis.location.pathname.startsWith(`${prefix}${PATHS.SIGN_IN}`)) {
              globalThis.location.assign(`${prefix}${PATHS.SIGN_IN}`);
            }
          } else {
            isRedirectInProgress = false;
          }
        }
        throw error;
      }
    );
  }

  return client;
};

const redirectExceptions = [PATHS.ACCESS_DENIED, PATHS.OAUTH2_REDIRECT, PATHS.OAUTH2_SIGNOUT];

const resetRedirectFlag = () => {
  isRedirectInProgress = false;
};

interface GlobalWithEventListeners {
  addEventListener: (type: string, listener: () => void) => void;
}

const hasEventListeners = (globalObject: unknown): globalObject is GlobalWithEventListeners => {
  return typeof globalObject === 'object' && globalObject !== null && 'addEventListener' in globalObject;
};

if (hasEventListeners(globalThis)) {
  globalThis.addEventListener('beforeunload', resetRedirectFlag);
  globalThis.addEventListener('popstate', resetRedirectFlag);
}

export const api = new Proxy({} as Api, {
  get: (_target, property) => {
    const client: HttpClient = createHttpClient();
    const freshApi: Api = new Api(client);
    return freshApi[property as keyof Api];
  },
});
